<template>
  <div class="startup-settings">
    <section class="startup-toolbar">
      <div class="startup-status">
        <span class="status-dot" :class="form.displayMode === 'FIXED' ? 'status-dot--fixed' : 'status-dot--random'"></span>
        <div>
          <strong>{{ modeLabel }}</strong>
          <span>{{ updatedText }}</span>
        </div>
      </div>
      <div class="toolbar-actions">
        <span v-if="message" class="save-message" :class="{ 'save-message--error': messageError }">{{ message }}</span>
        <button class="ghost-button" type="button" :disabled="loading || saving" @click="reload">刷新</button>
        <button class="primary-button" type="button" :disabled="!canEdit || loading || saving" @click="save">
          {{ saving ? "保存中…" : "保存设置" }}
        </button>
      </div>
    </section>

    <section class="mode-card">
      <div class="mode-card__copy">
        <span class="eyebrow">启动图展示方式</span>
        <h2>{{ modeLabel }}</h2>
        <p>{{ modeDescription }}</p>
      </div>
      <div class="mode-switch" role="group" aria-label="启动图展示方式">
        <button
          v-for="mode in modes"
          :key="mode.value"
          type="button"
          :disabled="!canEdit"
          :class="{ active: form.displayMode === mode.value }"
          @click="chooseMode(mode.value)"
        >
          <strong>{{ mode.label }}</strong>
          <span>{{ mode.hint }}</span>
        </button>
      </div>
    </section>

    <section class="workspace">
      <article class="image-library">
        <header class="panel-heading">
          <div>
            <h3>小程序图册</h3>
            <p>共 {{ images.length }} 张，点击图片即可设为固定启动图。</p>
          </div>
          <label class="image-search">
            <span class="sr-only">搜索图片</span>
            <input v-model.trim="keyword" type="search" placeholder="搜索图片名称" />
          </label>
        </header>

        <div v-if="loading" class="empty-state">正在读取图册…</div>
        <div v-else-if="!filteredImages.length" class="empty-state">
          {{ images.length ? "没有找到匹配的图片" : "图册中还没有可用图片，请先在公司项目中上传" }}
        </div>
        <div v-else class="image-grid">
          <button
            v-for="image in filteredImages"
            :key="String(image.id)"
            type="button"
            class="image-card"
            :class="{ selected: form.displayMode === 'FIXED' && form.screenId === image.id }"
            :disabled="!canEdit"
            @click="selectImage(image)"
          >
            <span class="image-card__preview">
              <img :src="resolveImage(image.imageUrl)" :alt="image.name || '启动图'" />
              <span v-if="form.displayMode === 'FIXED' && form.screenId === image.id" class="selected-mark">当前启动图</span>
            </span>
            <span class="image-card__copy">
              <strong>{{ image.name || "未命名图片" }}</strong>
              <small>{{ dimensionText(image) }}</small>
            </span>
          </button>
        </div>
      </article>

      <aside class="preview-panel">
        <header class="panel-heading">
          <div>
            <h3>展示预览</h3>
            <p>{{ form.displayMode === "FIXED" ? "小程序只会显示这一张" : "每次进入时从图册中随机显示" }}</p>
          </div>
        </header>
        <div class="phone-preview">
          <div class="phone-speaker"></div>
          <img
            v-if="previewImage"
            :src="resolveImage(previewImage.imageUrl)"
            :alt="previewImage.name || '启动页预览'"
          />
          <div v-else class="phone-empty">暂无预览</div>
        </div>
        <div class="preview-caption">
          <strong>{{ previewImage ? (previewImage.name || "未命名图片") : "未选择图片" }}</strong>
          <span>{{ form.displayMode === "FIXED" ? "固定展示" : "随机图池示例" }}</span>
        </div>
        <button
          v-if="form.displayMode === 'FIXED' && previewImage"
          class="phone-action"
          type="button"
          :disabled="!canEdit"
          @click="chooseMode('RANDOM')"
        >恢复随机展示</button>
      </aside>
    </section>
  </div>
</template>

<script>
import { fetchStartupImageConfig, saveStartupImageConfig } from "../api/officeDesign"
import { hasAdminPermission } from "../utils/adminSession"
import { resolveBackendAssetFallbackUrl, resolveBackendAssetUrl } from "../utils/apiEndpoint"

export default {
  name: "StartupSettingsView",
  data() {
    return {
      loading: false,
      saving: false,
      message: "",
      messageError: false,
      keyword: "",
      images: [],
      updatedAt: "",
      canEdit: hasAdminPermission("operation:theme"),
      form: {
        displayMode: "RANDOM",
        screenId: null
      },
      modes: [
        { value: "RANDOM", label: "随机展示", hint: "从全部启动图中随机抽取" },
        { value: "FIXED", label: "固定一张", hint: "每次只显示指定图片" }
      ]
    }
  },
  computed: {
    modeLabel() {
      return this.form.displayMode === "FIXED" ? "固定展示一张" : "随机展示"
    },
    modeDescription() {
      return this.form.displayMode === "FIXED"
        ? "用户每次打开小程序都会看到选中的这张图片。"
        : "用户每次打开小程序时，会从当前图册中随机展示一张。"
    },
    updatedText() {
      return this.updatedAt ? `上次保存 ${this.formatDateTime(this.updatedAt)}` : "尚未保存过固定设置"
    },
    filteredImages() {
      const keyword = this.keyword.toLowerCase()
      if (!keyword) return this.images
      return this.images.filter(image => String(image.name || "").toLowerCase().includes(keyword))
    },
    previewImage() {
      if (!this.images.length) return null
      if (this.form.displayMode === "FIXED" && this.form.screenId) {
        return this.images.find(image => image.id === this.form.screenId) || this.images[0]
      }
      return this.images[0]
    }
  },
  created() {
    this.reload()
  },
  methods: {
    async reload() {
      this.loading = true
      this.message = ""
      try {
        const res = await fetchStartupImageConfig()
        this.applyConfig(res.data || {})
      } catch (error) {
        this.messageError = true
        this.message = (error && error.message) || "启动页设置读取失败"
      } finally {
        this.loading = false
      }
    },
    applyConfig(config) {
      this.images = Array.isArray(config.images) ? config.images : []
      this.updatedAt = config.updatedAt || ""
      this.form = {
        displayMode: config.displayMode === "FIXED" ? "FIXED" : "RANDOM",
        screenId: config.screenId || null
      }
    },
    chooseMode(mode) {
      if (!this.canEdit) return
      this.form.displayMode = mode
      if (mode === "FIXED" && !this.form.screenId && this.images.length) {
        this.form.screenId = this.images[0].id
      }
      this.message = ""
    },
    selectImage(image) {
      if (!this.canEdit) return
      this.form.displayMode = "FIXED"
      this.form.screenId = image.id
      this.message = ""
    },
    async save() {
      if (this.form.displayMode === "FIXED" && !this.form.screenId) {
        this.messageError = true
        this.message = "请先选择一张启动图"
        return
      }
      this.saving = true
      this.message = ""
      try {
        const res = await saveStartupImageConfig({
          displayMode: this.form.displayMode,
          screenId: this.form.displayMode === "FIXED" ? this.form.screenId : null
        })
        this.applyConfig(res.data || {})
        this.messageError = false
        this.message = "已保存，下次启动小程序时生效"
      } catch (error) {
        this.messageError = true
        this.message = (error && error.message) || "保存失败"
      } finally {
        this.saving = false
      }
    },
    resolveImage(value) {
      return resolveBackendAssetFallbackUrl(resolveBackendAssetUrl(value))
    },
    dimensionText(image) {
      if (!image || !image.widthPx || !image.heightPx) return "图片尺寸未记录"
      return `${image.widthPx} × ${image.heightPx}`
    },
    formatDateTime(value) {
      if (!value) return ""
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      const pad = number => String(number).padStart(2, "0")
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.startup-settings {
  color: #18231f;
}

.startup-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
}

.startup-status,
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.startup-status > div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.startup-status strong {
  font-size: 14px;
}

.startup-status span:last-child {
  color: #81908a;
  font-size: 12px;
}

.status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #2d8f68;
  box-shadow: 0 0 0 5px rgba(45, 143, 104, 0.1);
}

.status-dot--fixed {
  background: #c38239;
  box-shadow: 0 0 0 5px rgba(195, 130, 57, 0.12);
}

.save-message {
  color: #2d7d5e;
  font-size: 12px;
}

.save-message--error {
  color: #bd3e39;
}

.mode-card {
  display: grid;
  grid-template-columns: minmax(220px, 0.8fr) minmax(420px, 1.2fr);
  gap: 28px;
  align-items: center;
  padding: 24px 26px;
  margin-bottom: 16px;
  border: 1px solid #e2e9e5;
  border-radius: 18px;
  background:
    radial-gradient(circle at 96% 10%, rgba(126, 164, 144, 0.16), transparent 32%),
    #fff;
}

.eyebrow {
  color: #678070;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.mode-card h2 {
  margin: 8px 0 5px;
  font-size: 23px;
}

.mode-card p,
.panel-heading p {
  margin: 0;
  color: #7d8b85;
  font-size: 12px;
  line-height: 1.6;
}

.mode-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 5px;
  border: 1px solid #e2e8e5;
  border-radius: 15px;
  background: #f5f7f6;
}

.mode-switch button {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 14px 16px;
  border: 1px solid transparent;
  border-radius: 11px;
  background: transparent;
  color: #5e6e67;
  text-align: left;
  cursor: pointer;
}

.mode-switch button strong {
  color: #31413a;
  font-size: 14px;
}

.mode-switch button span {
  font-size: 11px;
}

.mode-switch button.active {
  border-color: #cfdad4;
  background: #fff;
  box-shadow: 0 7px 18px rgba(31, 67, 51, 0.08);
}

.mode-switch button.active strong {
  color: #226e50;
}

.workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 310px;
  gap: 16px;
  align-items: start;
}

.image-library,
.preview-panel {
  border: 1px solid #e4e9e6;
  border-radius: 18px;
  background: #fff;
}

.image-library {
  min-width: 0;
  padding: 20px;
}

.preview-panel {
  position: sticky;
  top: 0;
  padding: 20px;
}

.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 16px;
}

.panel-heading h3 {
  margin: 0 0 4px;
  font-size: 15px;
}

.image-search input {
  width: 190px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #dce4df;
  border-radius: 10px;
  outline: none;
  background: #fafcfb;
  font: inherit;
  font-size: 12px;
}

.image-search input:focus {
  border-color: #82a995;
  box-shadow: 0 0 0 3px rgba(82, 145, 113, 0.1);
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(126px, 1fr));
  gap: 12px;
}

.image-card {
  min-width: 0;
  overflow: hidden;
  padding: 0;
  border: 1px solid #e2e8e4;
  border-radius: 13px;
  background: #fff;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
}

.image-card:hover {
  transform: translateY(-2px);
  border-color: #a9c3b4;
  box-shadow: 0 8px 20px rgba(32, 71, 53, 0.09);
}

.image-card.selected {
  border-color: #2e8b63;
  box-shadow: 0 0 0 2px rgba(46, 139, 99, 0.12);
}

.image-card__preview {
  position: relative;
  display: block;
  aspect-ratio: 9 / 13;
  overflow: hidden;
  background: #f0f3f1;
}

.image-card__preview img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: top;
}

.selected-mark {
  position: absolute;
  right: 7px;
  bottom: 7px;
  padding: 4px 7px;
  border-radius: 999px;
  background: rgba(24, 95, 66, 0.9);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
}

.image-card__copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 9px 10px 10px;
}

.image-card__copy strong,
.image-card__copy small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-card__copy strong {
  font-size: 12px;
}

.image-card__copy small {
  color: #8b9792;
  font-size: 10px;
}

.empty-state {
  display: grid;
  min-height: 220px;
  place-items: center;
  color: #8c9893;
  font-size: 13px;
}

.phone-preview {
  position: relative;
  width: 230px;
  aspect-ratio: 9 / 17.5;
  margin: 12px auto 18px;
  overflow: hidden;
  border: 8px solid #19231f;
  border-radius: 34px;
  background: #f5f2ed;
  box-shadow: 0 18px 40px rgba(25, 37, 31, 0.16);
}

.phone-speaker {
  position: absolute;
  z-index: 2;
  top: 7px;
  left: 50%;
  width: 52px;
  height: 5px;
  border-radius: 999px;
  background: rgba(20, 28, 24, 0.72);
  transform: translateX(-50%);
}

.phone-preview img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.phone-empty {
  display: grid;
  height: 100%;
  place-items: center;
  color: #8f9894;
  font-size: 12px;
}

.preview-caption {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.preview-caption strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

.preview-caption span {
  flex-shrink: 0;
  color: #76867f;
  font-size: 11px;
}

.phone-action {
  width: 100%;
  height: 38px;
  margin-top: 14px;
  border: 1px solid #d9e2dd;
  border-radius: 11px;
  background: #f8faf9;
  color: #486158;
  cursor: pointer;
}

.ghost-button,
.primary-button {
  height: 38px;
  padding: 0 16px;
  border-radius: 11px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.ghost-button {
  border: 1px solid #d9e2dd;
  background: #fff;
  color: #43574f;
}

.primary-button {
  border: 1px solid #236c4e;
  background: #236c4e;
  color: #fff;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 960px) {
  .mode-card,
  .workspace {
    grid-template-columns: 1fr;
  }

  .preview-panel {
    position: static;
  }

  .phone-preview {
    width: 205px;
  }
}

@media (max-width: 640px) {
  .startup-toolbar,
  .panel-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .toolbar-actions {
    flex-wrap: wrap;
  }

  .save-message {
    width: 100%;
  }

  .mode-card {
    padding: 18px;
  }

  .mode-switch {
    grid-template-columns: 1fr;
  }

  .image-library,
  .preview-panel {
    padding: 15px;
  }

  .image-search input {
    width: 100%;
  }

  .image-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
